import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ReportContext, ReportReceipt, ReportSubmission } from './feedback.types';

/** Screenshots above this are refused client-side rather than after the upload. */
export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;

@Injectable({ providedIn: 'root' })
export class FeedbackService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/feedback`;

  /**
   * Read the reporter's environment off the browser.
   *
   * Everything here is defensive: a browser may withhold any of it, and a
   * throw while gathering diagnostics would lose the report itself.
   */
  captureContext(): ReportContext {
    return {
      url: window.location.href,
      user_agent: navigator.userAgent ?? '',
      platform: this.describePlatform(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      screen_width: window.screen?.width ?? 0,
      screen_height: window.screen?.height ?? 0,
      device_pixel_ratio: window.devicePixelRatio ?? 1,
      timezone: this.resolveTimezone(),
      language: navigator.language ?? '',
    };
  }

  /** Submit a report. Multipart, because it may carry a screenshot. */
  submit(submission: ReportSubmission): Observable<ReportReceipt> {
    const form = new FormData();
    form.append('kind', submission.kind);
    form.append('message', submission.message);
    if (submission.tag) {
      form.append('tag', submission.tag);
      form.append('context', JSON.stringify(submission.context ?? {}));
    }

    const context = this.captureContext();
    for (const [key, value] of Object.entries(context)) {
      form.append(key, String(value));
    }
    if (submission.screenshot) {
      form.append('screenshot', submission.screenshot, 'screenshot.png');
    }
    return this.http.post<ReportReceipt>(`${this.base}/reports/`, form);
  }

  /**
   * A human-readable device hint.
   *
   * Prefers the User-Agent Client Hints platform, which is the supported
   * replacement for the deprecated `navigator.platform`; falls back to the
   * latter where hints are unavailable (Safari, Firefox).
   */
  private describePlatform(): string {
    const hints = (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData;
    return hints?.platform || navigator.platform || '';
  }

  private resolveTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    } catch {
      return '';
    }
  }
}
